import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import {
  Checkbox,
  Dropdown,
  Icon,
  Message,
  Modal,
  Table
} from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import Button from '../../components/Button';
import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { FieldLabel } from '../../styles';
import {
  importWalletProperties,
  ImportWalletPropertyPayload,
  ImportWalletPropertiesResult
} from '../../services/properties';
import { walletPropertyTypeOptions } from '../properties-wallet/constants';
import { successNotification, warningNotification } from '../../services/notification';
import {
  ActionsBar,
  ActionsGroup,
  ContentSegment,
  DataCell,
  Dropzone,
  ExpectedFieldsTable,
  HeaderPreview,
  ImportShell,
  MatchColumn,
  MatchGrid,
  MatchPreview,
  MatchSelect,
  MutedText,
  PageContainer,
  PageHeader,
  PageSubtitle,
  PageTitle,
  ScrollArea,
  StatusMessage,
  StepCircle,
  StepConnector,
  StepLabel,
  StepsBar,
  StyledTable,
  TableWrapper,
  ValidationTools
} from './styles';

type Step = 1 | 2 | 3 | 4;

type ImportField = {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
  example: string;
};

type ImportedRow = {
  rowNumber: number;
  values: Record<string, string>;
  parsed: {
    identification: string;
    propertyType: string;
    area?: number;
    zipcode: string;
    state: string;
    city: string;
    street: string;
    neighborhood: string;
    number: string;
    complement?: string;
  };
  errors: Partial<Record<string, string>>;
};

const fields: ImportField[] = [
  {
    key: 'identification',
    label: 'Identificação',
    required: true,
    aliases: ['identification', 'identificação', 'codigo', 'código', 'imóvel', 'imovel'],
    example: 'Ex.: Imóvel 100'
  },
  {
    key: 'propertyType',
    label: 'Tipo',
    required: true,
    aliases: ['type_id', 'tipo', 'tipo do imóvel', 'tipo do imovel', 'propertyType'],
    example: 'Ex.: Casa'
  },
  {
    key: 'area',
    label: 'Área',
    aliases: ['area', 'área'],
    example: 'Ex.: 100'
  },
  {
    key: 'zipcode',
    label: 'CEP',
    required: true,
    aliases: ['zipcode', 'cep', 'código postal', 'codigo postal', 'address.zipcode'],
    example: 'Ex.: 01000-000'
  },
  {
    key: 'street',
    label: 'Rua',
    required: true,
    aliases: ['street', 'rua', 'logradouro', 'address.street'],
    example: 'Ex.: Rua Dona Mariquita'
  },
  {
    key: 'number',
    label: 'Número',
    required: true,
    aliases: ['number', 'número', 'numero', 'address.number'],
    example: 'Ex.: 100'
  },
  {
    key: 'neighborhood',
    label: 'Bairro',
    required: true,
    aliases: ['neighborhood', 'bairro', 'address.neighborhood'],
    example: 'Ex.: Fazenda Morumbi'
  },
  {
    key: 'city',
    label: 'Cidade',
    required: true,
    aliases: ['city', 'cidade', 'address.city'],
    example: 'Ex.: São Paulo'
  },
  {
    key: 'state',
    label: 'UF',
    required: true,
    aliases: ['state', 'uf', 'estado', 'address.state'],
    example: 'Ex.: SP'
  },
  {
    key: 'complement',
    label: 'Complemento',
    aliases: ['complement', 'complemento', 'address.complement'],
    example: 'Ex.: Bloco 25'
  }
];

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const normalizeHeader = (value: string): string =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const formatCell = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value).trim();

const parseNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const resolvePropertyType = (value: string): string => {
  const normalizedValue = normalizeHeader(value);
  const option = walletPropertyTypeOptions.find(current => {
    return (
      normalizeHeader(String(current.value)) === normalizedValue ||
      normalizeHeader(String(current.text)) === normalizedValue
    );
  });

  return option ? String(option.value) : value;
};

const buildInitialMapping = (headers: string[]): Record<string, number | null> => {
  return fields.reduce<Record<string, number | null>>((acc, field) => {
    const headerIndex = headers.findIndex(header =>
      field.aliases.map(normalizeHeader).includes(normalizeHeader(header))
    );
    acc[field.key] = headerIndex >= 0 ? headerIndex : null;
    return acc;
  }, {});
};

const getMissingRequiredFields = (mapping: Record<string, number | null>) => {
  return fields.filter(field => field.required && mapping[field.key] === null);
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const maybeMessage = (error as { message?: string | string[] }).message;
    if (Array.isArray(maybeMessage)) return maybeMessage.join(', ');
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
  }

  if (error instanceof Error && error.message) return error.message;

  return 'Não foi possível ler a planilha.';
};

const PropertiesImportPage = ({ availableBrokersAndEstates, user }: Props) => {
  const history = useHistory();
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const defaultEstateId =
    availableBrokersAndEstates?.activeEstateId || user?.estate?.id || '';
  const [estateId, setEstateId] = useState(defaultEstateId);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [mapping, setMapping] = useState<Record<string, number | null>>({});
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [showRequiredModal, setShowRequiredModal] = useState(false);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [discardedRows, setDiscardedRows] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importResult, setImportResult] =
    useState<ImportWalletPropertiesResult | null>(null);

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map((item: CustomDropDownOptions) => {
      const valueId = typeof item.value === 'string' ? item.value : item.value?.id;
      return {
        key: valueId || String(item.key),
        text: item.text || '',
        value: valueId || ''
      };
    });
  }, [availableBrokersAndEstates?.estates]);

  const headers = useMemo(() => rawRows[headerRowIndex] || [], [
    rawRows,
    headerRowIndex
  ]);

  const dataRows = useMemo(() => rawRows.slice(headerRowIndex + 1), [
    rawRows,
    headerRowIndex
  ]);

  const importedRows = useMemo<ImportedRow[]>(() => {
    return dataRows
      .filter(row => row.some(cell => formatCell(cell)))
      .map((row, index) => {
        const values = fields.reduce<Record<string, string>>((acc, field) => {
          const columnIndex = mapping[field.key];
          acc[field.key] = columnIndex === null || columnIndex === undefined
            ? ''
            : formatCell(row[columnIndex]);
          return acc;
        }, {});

        const area = parseNumber(values.area);
        const parsed = {
          identification: values.identification,
          propertyType: resolvePropertyType(values.propertyType),
          area,
          zipcode: values.zipcode,
          state: values.state,
          city: values.city,
          street: values.street,
          neighborhood: values.neighborhood,
          number: values.number,
          complement: values.complement
        };
        const errors: Partial<Record<string, string>> = {};

        fields.forEach(field => {
          if (field.required && !values[field.key]) {
            errors[field.key] = `${field.label} obrigatório`;
          }
        });

        if (values.area && area === undefined) {
          errors.area = 'Área inválida';
        }

        if (values.propertyType && !resolvePropertyType(values.propertyType)) {
          errors.propertyType = 'Tipo inválido';
        }

        return {
          rowNumber: headerRowIndex + index + 2,
          values,
          parsed,
          errors
        };
      });
  }, [dataRows, headerRowIndex, mapping]);

  const missingRequiredFields = getMissingRequiredFields(mapping);
  const validRows = importedRows.filter(
    row => !discardedRows.includes(row.rowNumber) && Object.keys(row.errors).length === 0
  );
  const invalidRows = importedRows.filter(row => Object.keys(row.errors).length > 0);
  const visibleRows = importedRows.filter(row => {
    if (discardedRows.includes(row.rowNumber)) return false;
    if (!showOnlyErrors) return true;
    return Object.keys(row.errors).length > 0;
  });

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!isEstateUser && !estateId) {
      warningNotification('Atenção', 'Selecione a imobiliária antes de carregar a planilha.');
      return;
    }

    try {
      setParseError('');
      setImportResult(null);
      setFileName(file.name);
      setSelectedRows([]);
      setDiscardedRows([]);

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const parsedRows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: ''
      });
      const nonEmptyRows = parsedRows.filter(row => row.some(cell => formatCell(cell)));

      if (!nonEmptyRows.length) {
        throw new Error('Nenhum dado foi encontrado na planilha.');
      }

      setRawRows(nonEmptyRows);
      setHeaderRowIndex(0);
      setMapping(buildInitialMapping(nonEmptyRows[0] || []));
      setCurrentStep(2);
    } catch (error) {
      setRawRows([]);
      setMapping({});
      setParseError(getErrorMessage(error));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: '.xlsx,.xls,.csv'
  });

  const handleHeaderNext = () => {
    setMapping(buildInitialMapping(headers));
    setCurrentStep(3);
  };

  const handleMatchNext = () => {
    if (missingRequiredFields.length) {
      setShowRequiredModal(true);
      return;
    }
    setSelectedRows([]);
    setDiscardedRows([]);
    setCurrentStep(4);
  };

  const handleDiscardSelected = () => {
    setDiscardedRows(prev => Array.from(new Set([...prev, ...selectedRows])));
    setSelectedRows([]);
  };

  const handleImport = async () => {
    if (!isEstateUser && !estateId) {
      warningNotification('Atenção', 'Selecione a imobiliária para importar.');
      return;
    }

    if (!validRows.length) {
      warningNotification('Atenção', 'Não há linhas válidas para importar.');
      return;
    }

    const payload: ImportWalletPropertyPayload[] = validRows.map(row => ({
      estateId: isEstateUser ? undefined : estateId,
      identification: row.parsed.identification,
      propertyType: row.parsed.propertyType,
      area: row.parsed.area,
      address: {
        zipcode: row.parsed.zipcode,
        state: row.parsed.state.toUpperCase(),
        city: row.parsed.city,
        street: row.parsed.street,
        neighborhood: row.parsed.neighborhood,
        number: row.parsed.number,
        complement: row.parsed.complement || undefined
      }
    }));

    try {
      setIsSubmitting(true);
      const result = await importWalletProperties(payload);
      setImportResult(result);

      if (result.failed.length) {
        warningNotification(
          'Importação concluída com pendências',
          `${result.success} imóveis importados e ${result.failed.length} com erro.`
        );
      } else {
        successNotification('Sucesso', `${result.success} imóveis importados com sucesso.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSelectedRow = (rowNumber: number) => {
    setSelectedRows(prev =>
      prev.includes(rowNumber)
        ? prev.filter(current => current !== rowNumber)
        : [...prev, rowNumber]
    );
  };

  const renderSteps = () => {
    const steps = [
      'Carregar ficheiro',
      'Selecionar linha de cabeçalho',
      'Corresponder colunas',
      'Validar os dados'
    ];

    return (
      <StepsBar>
        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const completed = currentStep > stepNumber;
          const active = currentStep === stepNumber;

          return (
            <React.Fragment key={label}>
              <StepLabel active={active || completed}>
                <StepCircle active={active} completed={completed}>
                  {completed ? <Icon name="check" /> : stepNumber}
                </StepCircle>
                {label}
              </StepLabel>
              {stepNumber < steps.length && <StepConnector completed={completed} />}
            </React.Fragment>
          );
        })}
      </StepsBar>
    );
  };

  const renderUploadStep = () => (
    <>
      <PageHeader>
        <PageTitle>Carregar ficheiro</PageTitle>
      </PageHeader>

      {!isEstateUser && (
        <div style={{ maxWidth: 420, marginBottom: 16 }}>
          <FieldLabel>Imobiliária</FieldLabel>
          <Dropdown
            fluid
            value={estateId}
            placeholder="Selecionar..."
            options={estateOptions}
            search
            selection
            onChange={(_, { value }) => setEstateId((value as string) || '')}
          />
        </div>
      )}

      <h3>Dados esperados:</h3>
      <MutedText>
        Terá a oportunidade de renomear ou remover colunas nos próximos passos.
      </MutedText>
      <ExpectedFieldsTable>
        {fields.map(field => (
          <div key={field.key}>
            <strong>{field.label}</strong>
            <span>{field.example}</span>
          </div>
        ))}
      </ExpectedFieldsTable>

      <Dropzone {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <div>
          <h3>Carregar ficheiro .xlsx, .xls ou .csv</h3>
          <Button type="button" text="Selecione o ficheiro" />
          {fileName && <p>{fileName}</p>}
        </div>
      </Dropzone>

      {parseError && (
        <StatusMessage negative content={parseError} style={{ marginTop: 16 }} />
      )}
    </>
  );

  const renderHeaderStep = () => (
    <>
      <PageHeader>
        <PageTitle>Selecionar linha de cabeçalho</PageTitle>
      </PageHeader>
      <TableWrapper>
        <StyledTable celled compact>
          <Table.Body>
            {rawRows.slice(0, 30).map((row, index) => (
              <Table.Row
                key={index}
                active={index === headerRowIndex}
                onClick={() => setHeaderRowIndex(index)}
              >
                <Table.Cell collapsing>
                  <Checkbox
                    radio
                    checked={index === headerRowIndex}
                    onChange={() => setHeaderRowIndex(index)}
                  />
                </Table.Cell>
                {row.slice(0, 20).map((cell, cellIndex) => (
                  <Table.Cell key={`${index}-${cellIndex}`}>
                    {formatCell(cell) || '-'}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </StyledTable>
      </TableWrapper>
    </>
  );

  const renderMatchStep = () => {
    const dropdownOptions = fields.map(field => ({
      key: field.key,
      value: field.key,
      text: field.required ? `${field.label} *` : field.label
    }));

    return (
      <>
        <PageHeader>
          <PageTitle>Corresponder colunas</PageTitle>
        </PageHeader>

        <h3>A sua tabela</h3>
        <ScrollArea>
          <MatchGrid columnCount={headers.length}>
            {headers.map((header, index) => (
              <MatchColumn key={`${header}-${index}`}>
                <HeaderPreview>
                  <strong>{header || `Coluna ${index + 1}`}</strong>
                  <button
                    type="button"
                    onClick={() =>
                      setMapping(prev => {
                        const next = { ...prev };
                        Object.keys(next).forEach(key => {
                          if (next[key] === index) next[key] = null;
                        });
                        return next;
                      })
                    }
                  >
                    x
                  </button>
                </HeaderPreview>
                {dataRows.slice(0, 2).map((row, rowIndex) => (
                  <MatchPreview key={rowIndex}>{formatCell(row[index]) || '-'}</MatchPreview>
                ))}
              </MatchColumn>
            ))}
          </MatchGrid>
        </ScrollArea>

        <h3>Tornar-se-á</h3>
        <ScrollArea allowDropdownOverflow>
          <MatchGrid columnCount={headers.length}>
            {headers.map((header, index) => {
              const mappedField = Object.keys(mapping).find(key => mapping[key] === index);

              return (
                <MatchColumn key={`${header}-${index}-map`}>
                  <MatchSelect
                    value={mappedField || ''}
                    onChange={event => {
                      const fieldKey = event.target.value;
                      setMapping(prev => {
                        const next = { ...prev };
                        Object.keys(next).forEach(key => {
                          if (next[key] === index) next[key] = null;
                        });
                        if (fieldKey) next[fieldKey] = index;
                        return next;
                      });
                    }}
                  >
                    <option value="">Selecione a coluna...</option>
                    {dropdownOptions.map(option => (
                      <option key={option.key} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </MatchSelect>
                </MatchColumn>
              );
            })}
          </MatchGrid>
        </ScrollArea>
      </>
    );
  };

  const renderValidationStep = () => (
    <>
      <PageHeader>
        <PageTitle>Validar os dados</PageTitle>
      </PageHeader>

      <ValidationTools>
        <Button
          light
          type="button"
          disabled={!selectedRows.length || isSubmitting}
          onClick={handleDiscardSelected}
          text="Descartar linhas selecionadas"
        />
        <Checkbox
          toggle
          label="Mostrar apenas linhas com erros"
          checked={showOnlyErrors}
          onChange={(_, { checked }) => setShowOnlyErrors(Boolean(checked))}
        />
      </ValidationTools>

      {invalidRows.length > 0 && (
        <Message
          warning
          content={`${invalidRows.length} linha(s) possuem campos obrigatórios ausentes ou inválidos.`}
        />
      )}

      <TableWrapper>
        <StyledTable celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              {fields.map(field => (
                <Table.HeaderCell key={field.key}>{field.label}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleRows.slice(0, 120).map(row => (
              <Table.Row key={row.rowNumber} negative={Object.keys(row.errors).length > 0}>
                <Table.Cell collapsing>
                  <Checkbox
                    checked={selectedRows.includes(row.rowNumber)}
                    onChange={() => toggleSelectedRow(row.rowNumber)}
                  />
                </Table.Cell>
                {fields.map(field => (
                  <DataCell key={field.key} hasError={Boolean(row.errors[field.key])}>
                    {row.values[field.key] || '-'}
                  </DataCell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </StyledTable>
      </TableWrapper>

      {importResult && (
        <StatusMessage
          positive={!importResult.failed.length}
          warning={Boolean(importResult.failed.length)}
          style={{ marginTop: 16 }}
        >
          <Message.Header>Resultado da importação</Message.Header>
          <p>
            {importResult.success} de {importResult.total} imóveis foram importados.
          </p>
          {importResult.failed.length > 0 && (
            <p>
              Falhas:{' '}
              {importResult.failed
                .slice(0, 5)
                .map(fail => `linha ${fail.rowNumber}: ${fail.message}`)
                .join(' | ')}
            </p>
          )}
        </StatusMessage>
      )}
    </>
  );

  return (
    <PageContainer>
      <ImportShell>
        {renderSteps()}
        <ContentSegment>
          {currentStep === 1 && renderUploadStep()}
          {currentStep === 2 && renderHeaderStep()}
          {currentStep === 3 && renderMatchStep()}
          {currentStep === 4 && renderValidationStep()}
        </ContentSegment>
        <ActionsBar>
          <ActionsGroup>
            {currentStep > 1 && (
              <Button
                light
                type="button"
                disabled={isSubmitting}
                onClick={() => setCurrentStep((currentStep - 1) as Step)}
                text="Voltar"
              />
            )}
            {currentStep === 1 && (
              <Button
                light
                type="button"
                disabled={isSubmitting}
                onClick={() => history.push('/admin/properties')}
                text="Cancelar"
              />
            )}
            {currentStep === 2 && (
              <Button type="button" onClick={handleHeaderNext} text="Próximo" />
            )}
            {currentStep === 3 && (
              <Button type="button" onClick={handleMatchNext} text="Próximo" />
            )}
            {currentStep === 4 && (
              <Button
                type="button"
                loading={isSubmitting}
                disabled={isSubmitting || !validRows.length}
                onClick={handleImport}
                text="Confirmar"
              />
            )}
          </ActionsGroup>
        </ActionsBar>
      </ImportShell>

      <Modal
        size="tiny"
        open={showRequiredModal}
        onClose={() => setShowRequiredModal(false)}
      >
        <Modal.Header>Nem todas as colunas correspondem</Modal.Header>
        <Modal.Content>
          <p>
            Existem colunas obrigatórias que não são correspondidas ou ignoradas.
          </p>
          <p>
            Colunas não correspondidas:{' '}
            <strong>{missingRequiredFields.map(field => field.label).join(', ')}</strong>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            light
            type="button"
            text="Cancelar"
            onClick={() => setShowRequiredModal(false)}
          />
        </Modal.Actions>
      </Modal>
    </PageContainer>
  );
};

export default connect(mapState, null)(PropertiesImportPage);
